import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './TabCollecte.module.css';
import MetaHead from '../components/MetaHead';
import FeedbackPriveCard from '../components/FeedbackPriveCard/FeedbackPriveCard';
import { LANG_LABELS, SUPPORTED_LANGS } from '../i18n/config';
import { formatDate } from '../utils/format';

/* ============================================================
   TabCollecte — onglet "Collecter des avis"
   ------------------------------------------------------------
   IDs DOM préservés (backend Express) :
     #f-prenom · #f-tel · #f-service · #f-message · #f-delai
     #contacts-tbody · #sms-quota-widget · #recent-reviews
     #sms-preview-bubble · #sms-counter

   Callbacks :
     onSubmit(payload)        ← envoyerSMS(formData)
     onPreviewChange(message) ← updatePreview(text)
     onExportCSV()            ← exporterContactsCSV()

   i18n P5b :
     - useTranslation('tabs') + MetaHead page="collecte"
     - Ajout colonne "Langue" + filtre langue (drapeau via LANG_LABELS)
     - Backend retourne désormais `lang` dans sms-log (LATERAL JOIN clients_qr)
   ============================================================ */

const TEMPLATE_DEFAULT_KEY = 'collecte.template_default';

function formatPhone(raw = '') {
  const d = raw.replace(/\D/g, '').slice(0, 10);
  return d.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
}

/** Renvoie le drapeau + label pour une langue donnée (ou fallback). */
function langMeta(lang) {
  if (!lang) return null;
  return LANG_LABELS[lang] || null;
}

/* ===========================================================
   FormSection — champ titré
=========================================================== */
function FormSection({ step, title, sub, children }) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHead}>
        <span className={styles.stepBadge}>{step}</span>
        <div>
          <h3 className={styles.sectionTitle}>{title}</h3>
          {sub && <p className={styles.sectionSub}>{sub}</p>}
        </div>
      </div>
      <div className={styles.sectionBody}>{children}</div>
    </div>
  );
}

/* ===========================================================
   PhonePreview — aperçu SMS iPhone-like
=========================================================== */
function PhonePreview({ message, prenom, t }) {
  const defaultName = t('collecte.preview_default_name');
  const rendered = useMemo(() => {
    return (message || '')
      .replace(/{prenom}/gi, prenom?.trim() || defaultName)
      .replace(/{lien_avis}/gi, 'g.page/r/abc');
  }, [message, prenom, defaultName]);

  const charCount = (message || '').length;
  const smsCount  = Math.max(1, Math.ceil(charCount / 160));

  return (
    <aside className={styles.previewWrap}>
      <div className={styles.previewLabel}>{t('collecte.preview_label')}</div>
      <div className={styles.phone}>
        <div className={styles.phoneNotch} />
        <div className={styles.phoneScreen}>
          <div className={styles.smsHeader}>
            <div className={styles.smsAvatar}>R</div>
            <div className={styles.smsName}>Revyo</div>
          </div>
          <div id="sms-preview-bubble" className={styles.smsBubble}>
            {rendered || <span className={styles.smsPlaceholder}>{t('collecte.preview_placeholder')}</span>}
          </div>
          <div className={styles.smsTime}>{t('collecte.preview_now')}</div>
        </div>
      </div>
      <div id="sms-counter" className={styles.smsCounter}>
        <span><strong>{charCount}</strong> {t('collecte.preview_chars_short')}</span>
        <span className={styles.dot}>·</span>
        <span><strong>{smsCount}</strong> {t('collecte.preview_sms_short')}</span>
      </div>
    </aside>
  );
}

/* ===========================================================
   ContactRow — ligne d'historique
=========================================================== */
function ContactRow({ contact, t, lang }) {
  const status = contact.status || contact.statut || 'sent';
  const statusMap = {
    sent:      { labelKey: 'collecte.status_sent',     cls: styles.statSent },
    clicked:   { labelKey: 'collecte.status_clicked',  cls: styles.statClicked },
    reviewed:  { labelKey: 'collecte.status_reviewed', cls: styles.statReviewed },
    failed:    { labelKey: 'collecte.status_failed',   cls: styles.statFailed },
    pending:   { labelKey: 'collecte.status_pending',  cls: styles.statPending },
  };
  const st = statusMap[status] || statusMap.sent;
  const meta = langMeta(contact.lang);

  return (
    <tr>
      <td className={styles.tdName} data-label={t('collecte.table_col_prenom')}>{contact.prenom || '—'}</td>
      <td className={styles.tdMono} data-label={t('collecte.table_col_tel')}>{formatPhone(contact.tel || contact.telephone || '')}</td>
      <td data-label={t('collecte.table_col_service')}>{contact.service || '—'}</td>
      <td className={styles.tdDate} data-label={t('collecte.table_col_date')}>{contact.created_at
        ? formatDate(contact.created_at, lang, { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
        : (contact.date || '—')}</td>
      <td data-label={t('collecte.table_col_status')}>
        <span className={`${styles.statusPill} ${st.cls}`}>{t(st.labelKey)}</span>
      </td>
      <td data-label={t('collecte.table_col_lang')} title={meta ? meta.label : t('collecte.lang_unknown')} aria-label={meta ? meta.label : t('collecte.lang_unknown')}>
        {meta ? meta.flag : '—'}
      </td>
    </tr>
  );
}

/* ===========================================================
   DelaiSmsBlock — encart "Délai d'envoi du SMS après le repas"
=========================================================== */
function DelaiSmsBlock({ value, onChange, t }) {
  const OPTIONS = [
    { value: 60,   label: t('collecte.delai_opt_1h') },
    { value: 180,  label: t('collecte.delai_opt_3h') },
    { value: 1440, label: t('collecte.delai_opt_24h') },
  ];
  const [saving, setSaving]   = useState(false);
  const [toast, setToast]     = useState(null);
  const [pending, setPending] = useState(null);

  const handlePick = useCallback(async (v) => {
    if (saving || v === value) return;
    setSaving(true);
    setPending(v);
    try {
      await onChange(v);
      setToast(t('collecte.delai_saved'));
      setTimeout(() => setToast(null), 2400);
    } catch (e) {
      setToast(t('collecte.delai_err_template', { message: e?.message || t('collecte.delai_err_default') }));
      setTimeout(() => setToast(null), 3000);
    } finally {
      setSaving(false);
      setPending(null);
    }
  }, [onChange, saving, value, t]);

  return (
    <section className={styles.delaiBlock} aria-labelledby="delai-sms-title">
      <div className={styles.delaiHead}>
        <div>
          <h3 id="delai-sms-title" className={styles.delaiTitle}>{t('collecte.delai_title')}</h3>
          <p className={styles.delaiSub}>{t('collecte.delai_sub')}</p>
        </div>
      </div>
      <div className={styles.delaiPills} role="radiogroup" aria-label={t('collecte.delai_aria_group')}>
        {OPTIONS.map((o) => {
          const isActive = value === o.value;
          const isPending = pending === o.value;
          return (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={isActive}
              disabled={saving}
              className={`${styles.delaiPill} ${isActive ? styles.delaiPillActive : ''}`}
              onClick={() => handlePick(o.value)}
            >
              {isPending ? '…' : o.label}
            </button>
          );
        })}
      </div>
      {toast && <div role="status" aria-live="polite" className={styles.delaiToast}>{toast}</div>}
    </section>
  );
}

/* ===========================================================
   TabCollecte
=========================================================== */
export default function TabCollecte({
  // état
  contacts = [],
  recentReviews = [],
  feedbacksPrives = [],
  smsQuota = { used: 0, total: 200 },
  completion = null,
  delaiEnvoiMinutes = 60,

  // callbacks
  onSubmit,
  onPreviewChange,
  onExportCSV,
  onUpdateDelaiEnvoi,
}) {
  const { t, i18n } = useTranslation('tabs');
  const lang = i18n.language;

  const SERVICES = [
    { value: '',          label: t('collecte.service_placeholder') },
    { value: 'midi',      label: t('collecte.service_midi') },
    { value: 'soir',      label: t('collecte.service_soir') },
    { value: 'brunch',    label: t('collecte.service_brunch') },
    { value: 'evenement', label: t('collecte.service_evenement') },
    { value: 'livraison', label: t('collecte.service_livraison') },
  ];

  const DELAIS = [
    { value: '0',    label: t('collecte.delai_opt_immediat') },
    { value: '60',   label: t('collecte.delai_opt_1h_after') },
    { value: '180',  label: t('collecte.delai_opt_3h_after') },
    { value: '1440', label: t('collecte.delai_opt_next_day') },
  ];

  const [form, setForm] = useState(() => ({
    prenom: '',
    tel: '',
    service: '',
    delai: '60',
    message: t(TEMPLATE_DEFAULT_KEY),
  }));
  const [sending, setSending] = useState(false);
  const [langFilter, setLangFilter] = useState('all');

  const update = useCallback((k, v) => {
    setForm((prev) => {
      const next = { ...prev, [k]: v };
      if (k === 'message' && onPreviewChange) onPreviewChange(v);
      return next;
    });
  }, [onPreviewChange]);

  const canSend = form.prenom.trim() && form.tel.replace(/\D/g, '').length >= 9 && form.message.trim();

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!canSend || !onSubmit) return;
    setSending(true);
    try {
      await onSubmit({ ...form, tel: form.tel.replace(/\D/g, '') });
      setForm((p) => ({ ...p, prenom: '', tel: '' }));
    } finally {
      setSending(false);
    }
  }, [canSend, form, onSubmit]);

  // Sprint 2 fix : total = null → plan illimité (intensif). Pas de pourcentage à calculer.
  const isUnlimited = smsQuota.total === null;
  const quotaPct = isUnlimited
    ? 0
    : Math.min(100, (smsQuota.used / Math.max(1, smsQuota.total)) * 100);

  // Filtrage par langue côté UI (la backend ne filtre pas, c'est local)
  const filteredContacts = useMemo(() => {
    if (langFilter === 'all') return contacts;
    return contacts.filter((c) => (c.lang || null) === langFilter);
  }, [contacts, langFilter]);

  return (
    <>
      <MetaHead page="collecte" />
      <header className={styles.tabHeader}>
        <div className={styles.titleBlock}>
          <h1><span className={styles.titleEmoji}>{t('collecte.title_emoji')}</span>{t('collecte.title')}</h1>
          <p>{t('collecte.subtitle')}</p>
        </div>
        <div className={styles.actions}>
          <button type="button" className={styles.btnGhost} onClick={onExportCSV}>
            {t('collecte.btn_export_csv')}
          </button>
        </div>
      </header>

      {/* ===== Taux de complétion (vraie métrique de perf — recadre le SMS) ===== */}
      {completion && completion.scans > 0 && (
        <div id="completion-widget" className={styles.quotaWidget} style={{ borderColor: '#BFE3C6' }}>
          <div className={styles.quotaInfo}>
            <span className={styles.quotaLabel}>{t('collecte.completion_label')}</span>
            <strong>{completion.taux}%</strong>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
            {t('collecte.completion_sub', { avis: completion.avisGeneres, scans: completion.scans })}
          </div>
        </div>
      )}

      {/* ===== Quota SMS (canal de relance secondaire post-V3) ===== */}
      <div id="sms-quota-widget" className={styles.quotaWidget}>
        <div className={styles.quotaInfo}>
          <span className={styles.quotaLabel}>{t('collecte.quota_label')}</span>
          {isUnlimited ? (
            <strong>
              {t('collecte.quota_unlimited_unit', { count: smsQuota.used })}{' '}
              <span className={styles.quotaTotal}>{t('collecte.quota_unlimited_suffix')}</span>
            </strong>
          ) : (
            <strong>{smsQuota.used} <span className={styles.quotaTotal}>/ {smsQuota.total}</span></strong>
          )}
        </div>
        {!isUnlimited && (
          <div className={styles.quotaBar}>
            <div className={styles.quotaFill} style={{ width: `${quotaPct}%` }} />
          </div>
        )}
        <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 6 }}>{t('collecte.quota_caption')}</div>
      </div>

      {/* ===== Sprint 1 ticket 2 — Délai d'envoi SMS (config persistée) ===== */}
      {onUpdateDelaiEnvoi && (
        <DelaiSmsBlock value={delaiEnvoiMinutes} onChange={onUpdateDelaiEnvoi} t={t} />
      )}

      {/* ===== Form + Preview ===== */}
      <div className={styles.formGrid}>
        <form className={styles.formCard} onSubmit={handleSubmit}>
          <FormSection step="1" title={t('collecte.section_1_title')} sub={t('collecte.section_1_sub')}>
            <div className={styles.row}>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>{t('collecte.field_prenom_label')}</span>
                <input
                  id="f-prenom"
                  type="text"
                  className={styles.input}
                  value={form.prenom}
                  onChange={(e) => update('prenom', e.target.value)}
                  placeholder={t('collecte.field_prenom_placeholder')}
                  autoComplete="off"
                />
              </label>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>{t('collecte.field_tel_label')}</span>
                <input
                  id="f-tel"
                  type="tel"
                  className={styles.input}
                  value={formatPhone(form.tel)}
                  onChange={(e) => update('tel', e.target.value)}
                  placeholder={t('collecte.field_tel_placeholder')}
                  autoComplete="off"
                />
              </label>
            </div>
          </FormSection>

          <FormSection step="2" title={t('collecte.section_2_title')} sub={t('collecte.section_2_sub')}>
            <div className={styles.row}>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>{t('collecte.field_service_label')}</span>
                <select
                  id="f-service"
                  className={styles.select}
                  value={form.service}
                  onChange={(e) => update('service', e.target.value)}
                >
                  {SERVICES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </label>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>{t('collecte.field_delai_label')}</span>
                <select
                  id="f-delai"
                  className={styles.select}
                  value={form.delai}
                  onChange={(e) => update('delai', e.target.value)}
                >
                  {DELAIS.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </label>
            </div>
          </FormSection>

          <FormSection
            step="3"
            title={t('collecte.section_3_title')}
            sub={t('collecte.section_3_sub')}
          >
            <textarea
              id="f-message"
              className={styles.textarea}
              rows={4}
              value={form.message}
              onChange={(e) => update('message', e.target.value)}
              placeholder={t('collecte.msg_placeholder')}
            />
            <div className={styles.tplHint}>
              {t('collecte.tpl_hint_label')}
              <button type="button" className={styles.chip} onClick={() => update('message', form.message + ' {prenom}')}>{'{prenom}'}</button>
              <button type="button" className={styles.chip} onClick={() => update('message', form.message + ' {lien_avis}')}>{'{lien_avis}'}</button>
            </div>
          </FormSection>

          <div className={styles.formFooter}>
            <button
              type="submit"
              className={styles.btnPrimary}
              disabled={!canSend || sending}
            >
              {sending ? t('collecte.btn_sending') : t('collecte.btn_send')}
            </button>
          </div>
        </form>

        <PhonePreview message={form.message} prenom={form.prenom} t={t} />
      </div>

      {/* ===== Avis récents ===== */}
      <section id="recent-reviews" className={styles.recentBlock}>
        <header className={styles.blockHead}>
          <h2>{t('collecte.recent_reviews_title')}</h2>
          <span className={styles.blockCount}>{recentReviews.length}</span>
        </header>
        {recentReviews.length === 0 ? (
          <div className={styles.emptySmall}>
            {t('collecte.recent_empty')}
          </div>
        ) : (
          <div className={styles.recentList}>
            {recentReviews.slice(0, 5).map((rv, i) => (
              <div key={rv.id ?? i} className={styles.recentItem}>
                <div className={styles.recentStars}>
                  {'★'.repeat(rv.rating || 5)}<span className={styles.recentStarsDim}>{'★'.repeat(5 - (rv.rating || 5))}</span>
                </div>
                <div className={styles.recentBody}>
                  <strong>{rv.author || t('collecte.anonymous_client_short')}</strong>
                  {rv.text && <p>"{rv.text}"</p>}
                </div>
                <div className={styles.recentDate}>{rv.date || ''}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ===== Funnel V2 — Feedbacks privés reçus ===== */}
      <section className={styles.feedbacksBlock}>
        <header className={styles.blockHead}>
          <h2>{t('collecte.feedbacks_title')}</h2>
          <span className={styles.blockCount}>{feedbacksPrives.length}</span>
        </header>
        {feedbacksPrives.length === 0 ? (
          <div className={styles.emptySmall}>
            {t('collecte.feedbacks_empty')}
          </div>
        ) : (
          <div className={styles.feedbacksList}>
            {feedbacksPrives.slice(0, 20).map((f) => (
              <FeedbackPriveCard key={f.id} feedback={f} />
            ))}
          </div>
        )}
      </section>

      {/* ===== Historique ===== */}
      <section className={styles.tableBlock}>
        <header className={styles.blockHead}>
          <h2>{t('collecte.history_title')}</h2>
          <span className={styles.blockCount}>{filteredContacts.length}</span>
        </header>

        {/* Filtre langue (drapeau + label natif depuis i18n/config) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px', marginBottom: 4 }}>
          <label htmlFor="contacts-lang-filter" style={{ fontSize: 13, color: '#6E6C62' }}>
            {t('collecte.lang_filter_label')}
          </label>
          <select
            id="contacts-lang-filter"
            value={langFilter}
            onChange={(e) => setLangFilter(e.target.value)}
            style={{
              padding: '6px 10px',
              border: '1px solid #E5E1D8',
              borderRadius: 8,
              background: '#FFF',
              fontSize: 13,
              color: '#18160F',
              cursor: 'pointer',
            }}
          >
            <option value="all">{t('collecte.lang_filter_all')}</option>
            {SUPPORTED_LANGS.map((lg) => {
              const meta = LANG_LABELS[lg];
              return (
                <option key={lg} value={lg}>
                  {meta.flag} {meta.native}
                </option>
              );
            })}
          </select>
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('collecte.table_col_prenom')}</th>
                <th>{t('collecte.table_col_tel')}</th>
                <th>{t('collecte.table_col_service')}</th>
                <th>{t('collecte.table_col_date')}</th>
                <th>{t('collecte.table_col_status')}</th>
                <th>{t('collecte.table_col_lang')}</th>
              </tr>
            </thead>
            <tbody id="contacts-tbody">
              {filteredContacts.length === 0 ? (
                <tr>
                  <td colSpan={6} className={styles.tableEmpty}>
                    {t('collecte.table_empty')}
                  </td>
                </tr>
              ) : (
                filteredContacts.map((c, i) => <ContactRow key={c.id ?? i} contact={c} t={t} lang={lang} />)
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
